import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import useProjects from "../../../core/customhook/useProjects";
import useApi from "../../../core/customhook/useApi";
import { downloadExcelTemplate } from "../../../core/excelDownloadTemplate/downloadExcelTemplate";
import { all_routes } from "../../router/all_routes";
import SearchableSelect from "../../../core/common/SearchableSelect";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { Link } from "react-router-dom";

const MAX_FILE_SIZE_MB = 10;

const AddDepartment = () => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const navigate = useNavigate();

  const { projects } = useProjects();

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null); 

  const bulkUploadProgram = useApi<any>(
    `/api/v1/master/projects/${selectedProjectId}/programs/bulk`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const isUploading = bulkUploadProgram.loading;
  // ========== here is all excel upload state and funcation ===============

  const [readingExcel, setReadingExcel] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [excelRows, setExcelRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [validRows, setValidRows] = useState(0);
  const [invalidRows, setInvalidRows] = useState(0);
  const [sheetName, setSheetName] = useState("");
  const [filterData, setFilterData] = useState({
    account_name: "",
    program: "",
    dept_loc: "",
    package: "",
  });

  const [programList, setProgramList] = useState<any[]>([]);
  const [departmentList, setDepartmentList] = useState([]); 

  const REQUIRED_HEADERS = [
    "Account Code *",
    "Display Name *",
    "Location *",
    "Description *",
    "Is Active *",
  ];

  const readExcel = async (selectedFile: File) => {
    try {
      setReadingExcel(true);
      setUploadProgress(0);
      setFileError("");

      const data = await selectedFile.arrayBuffer();
      setUploadProgress(20);

      const workbook = XLSX.read(data);
      setUploadProgress(40);

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      setSheetName(workbook.SheetNames[0]);

      /* ---------------- HEADER VALIDATION ---------------- */

      const headerData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
      });

      const excelHeaders = (headerData[0] || []) as string[];

      const missingHeaders = REQUIRED_HEADERS.filter(
        (header) => !excelHeaders.includes(header),
      );

      if (missingHeaders.length > 0) {
        setReadingExcel(false);

        setFileError(
          `Invalid template. Missing/Changed column(s): ${missingHeaders.join(", ")}`,
        );

        setFile(null);
        setExcelRows([]);
        setTotalRows(0);
        setValidRows(0);
        setInvalidRows(0);

        return;
      }

      /* ---------------- READ DATA ---------------- */

      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
      });

      setUploadProgress(60);

      const filteredRows = jsonData.filter((row) =>
        Object.values(row).some((value) => String(value).trim() !== ""),
      );

      setTotalRows(filteredRows.length);

      /* ---------------- ROW VALIDATION ---------------- */

      const validPrograms: any[] = [];
      const invalidRowsList: any[] = [];

      filteredRows.forEach((row, index) => {
        const errors: string[] = [];

        if (!row["Account Code *"]?.toString().trim()) {
          errors.push("Account Code");
        }

        if (!row["Display Name *"]?.toString().trim()) {
          errors.push("Display Name");
        }

        if (!row["Location *"]?.toString().trim()) {
          errors.push("Location");
        }

        if (!row["Description *"]?.toString().trim()) {
          errors.push("Description");
        }

        const status = row["Is Active *"]?.toString().trim().toUpperCase();

        if (status !== "TRUE" && status !== "FALSE") {
          errors.push("Is Active");
        }

        if (errors.length > 0) {
          invalidRowsList.push({
            rowNumber: index + 2, 
            errors,
          });

          return;
        }

        validPrograms.push({
          program_code: row["Account Code *"].toString().trim(),
          program_name: row["Display Name *"].toString().trim(),
          location_name: row["Location *"].toString().trim(),
          description: row["Description *"].toString().trim(),
          is_active: status === "TRUE",
        });
      });

      setExcelRows(validPrograms);

      setValidRows(validPrograms.length);
      setInvalidRows(invalidRowsList.length);  
      setUploadProgress(100);
 
      setTimeout(() => {
        setReadingExcel(false);
      }, 300);
    } catch (error) { 
      setReadingExcel(false); 
      setFileError("Unable to read Excel file.");
    }
  };

  const handleDownloadTemplate = () => { 
    downloadExcelTemplate({
      fileName: "Program_Bulk_Upload_Template.xlsx", 
      sheetName: "Programs", 
      columns: [
        {
          header: "Account Code *",
          key: "program_code",
          width: 20,
        },

        {
          header: "Display Name *",
          key: "program_name",
          width: 35,
        },

        {
          header: "Location *",
          key: "location_name",
          width: 25,
        },

        {
          header: "Description *",
          key: "description",
          width: 40,
        },

        {
          header: "Is Active *",
          key: "is_active",
          width: 18,
        },
      ],

      dropdowns: [
        {
          key: "is_active",
          values: ["TRUE", "FALSE"],
        },
      ],
    }); 
  };

  const processFile = async (selectedFile: File) => {
    setFileError("");

    if (!selectedFile.name.toLowerCase().endsWith(".xlsx")) {
      setFileError("Please upload a valid .xlsx file.");
      return;
    }

    setFile(selectedFile);

    await readExcel(selectedFile);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) return;

    await processFile(selectedFile);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files?.[0];

    if (!droppedFile) return;

    await processFile(droppedFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileError(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleBulkUpload = async () => {
    if (!selectedProjectId) {
      return Swal.fire({
        icon: "warning",
        title: "Project Required",
        text: "Please select a project.",
      });
    }

    if (excelRows.length === 0) {
      return Swal.fire({
        icon: "warning",
        title: "Excel Required",
        text: "Please upload a valid Excel file.",
      });
    }

    try {
      const payload = {
        programs: excelRows,
      };

      const res = await bulkUploadProgram.callApi(payload);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Success", 
          text: res?.message,
        });

        navigate(all_routes.program);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: error?.response?.message ?? "Unable to upload programs.",
      });
    }
  };

  // ---------------- PROGRAMS (depends on selected brand) ----------------
  const getPrograms = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleBrandChange = async (projectId: string) => {
    setSelectedProjectId(projectId); 
    setFilterData((prev) => ({
      ...prev,
      account_name: projectId,
      program: "",
      dept_loc: "",
    }));
    setProgramList([]);
    setDepartmentList([]);

    if (!projectId) return;

    try {
      const res = await getPrograms.callApi(
        {},
        `/api/v1/master/projects/${projectId}/programs`,
      );

      if (res?.success) {
        setProgramList(res.data);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load programs.",
      });
    }
  };

  // ---------------- DEPARTMENTS (depends on selected program) ----------------
  const getDepartments = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleProgramChange = async (programId: string) => {
    setFilterData((prev) => ({
      ...prev,
      program: programId,
      dept_loc: "",
    }));

    setDepartmentList([]);

    if (!programId) return;

    try {
      const res = await getDepartments.callApi(
        {},
        `/api/v1/master/programs/${programId}/departments`,
      );

      if (res?.success) {
        setDepartmentList(res.data);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load departments.",
      });
    }
  };

  const handleFieldDataChange = (name: string, value: string | number) => {
    if (name === "program") {
      handleProgramChange(value as string);
      return;
    }

    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Add Department</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item active">Add Department</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white">
            <h4 className="mb-0 fw-bold">Bulk Program Upload</h4>
            <small className="text-muted">
              Upload multiple programs in a single Excel file.
            </small>
          </div>

          <div className="card-body">
            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <i
                  className="ti ti-info-circle-filled me-2"
                  style={{ color: "#d2660d", fontSize: "20px" }}
                />
                <h6 className="mb-0 fw-bold">Bulk Upload Instructions</h6>
              </div>

              <div className="d-flex flex-wrap gap-2">
                {[
                  "Use latest template",
                  "Don't edit headers",
                  "Upload .xlsx only",
                  "All fields mandatory",
                  "Select TRUE / FALSE only",
                ].map((item, index) => (
                  <span
                    key={index}
                    className="px-2 py-2 rounded-pill"
                    style={{
                      background: "#fdeaea",
                      borderColor: "#dc3545",
                      color: "#b02a37",
                      fontSize: "12px",
                      fontWeight: 700,
                    }}
                  >
                    <i className="ti ti-check me-1"></i>
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <hr
              style={{
                border: "none",
                height: "2px",
                background:
                  "linear-gradient(to right, transparent, #6d3300, #af5d00, #f26522, #f26522, #6d3300, transparent)",
                borderRadius: "999px",
                opacity: 1,
              }}
            />

            {/* STEP 1 */}
            <div className="p-4 mb-4">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="rounded-circle d-flex align-items-center bg-primary text-white justify-content-center me-3"
                  style={{ width: 45, height: 45 }}
                >
                  1
                </div>
                <div>
                  <h5 className="mb-1">Download Sample Template</h5>
                  <small className="text-muted">
                    Download the latest Excel template before entering your
                    data. Don't rename the column headers — they're used to map
                    your data during upload.
                  </small>
                </div>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleDownloadTemplate}
              >
                <i className="ti ti-download me-2"></i>
                Download Template
              </button>
            </div>

            <hr
              style={{
                border: "none",
                height: "2px",
                background:
                  "linear-gradient(to right, transparent, #6d3300, #af5d00, #f26522, #f26522, #6d3300, transparent)",
                borderRadius: "999px",
                opacity: 1,
              }}
            />
            <div className="p-4 mb-4">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="rounded-circle d-flex align-items-center bg-primary text-white justify-content-center me-3"
                  style={{ width: 45, height: 45 }}
                >
                  2
                </div>
                <div>
                  <h5 className="mb-1">Upload Completed Excel</h5>
                  <small className="text-muted">
                    Upload only .xlsx files generated from the sample template.
                    Max size {MAX_FILE_SIZE_MB} MB, one file at a time.
                  </small>
                </div>
              </div>

              {!file && (
                <div
                  className="border border-2 rounded text-center p-5"
                  style={{
                    borderStyle: "dashed",
                    borderColor: isDragging ? "#f26522" : undefined,
                    backgroundColor: isDragging
                      ? "rgba(13,110,253,0.05)"
                      : undefined,
                    cursor: "pointer",
                    transition: "all .15s ease-in-out",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      fileInputRef.current?.click();
                  }}
                >
                  <i
                    className="ti ti-file-spreadsheet"
                    style={{ fontSize: 55, color: "#f26522" }}
                  />
                  <h6 className="mt-3">Drag & Drop Excel Here</h6>
                  <p className="text-muted mb-3">
                    or{" "}
                    <span className="text-primary text-decoration-underline">
                      browse your computer
                    </span>
                  </p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    className="d-none"
                    onChange={handleFileChange}
                  />
                </div>
              )}

              {/* Reading Loader */}

              {readingExcel && (
                <div className="card mt-3 border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <div
                        className="spinner-border spinner-border-sm text-primary me-3"
                        role="status"
                      />

                      <div>
                        <h6 className="mb-1">Reading Excel File...</h6>

                        <small className="text-muted">
                          Please wait while we validate your spreadsheet.
                        </small>
                      </div>
                    </div>

                    <div className="progress" style={{ height: 8 }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        style={{
                          width: `${uploadProgress}%`,
                        }}
                      />
                    </div>

                    <div className="text-end mt-2">
                      <small className="text-muted">
                        {uploadProgress}% Completed
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {/* File Details */}

              {!readingExcel && file && (
                <div className="card mt-3 border">
                  <div className="card-body py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded bg-success-subtle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: 48,
                            height: 48,
                          }}
                        >
                          <i
                            className="ti ti-file-spreadsheet text-success"
                            style={{
                              fontSize: 28,
                            }}
                          />
                        </div>

                        <div>
                          <h6 className="mb-1">{file.name}</h6>

                          <small className="text-muted">
                            {(file.size / 1024).toFixed(1)} KB
                          </small>
                        </div>
                      </div>

                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleRemoveFile}
                      >
                        <i className="ti ti-trash me-1"></i>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Excel Summary */}

              {!readingExcel && excelRows.length > 0 && (
                <div className="card mt-3 border">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="ti ti-table me-2"></i>
                      Excel Summary
                    </h6>
                  </div>

                  <div className="card-body">
                    <div className="row text-center">
                      <div className="col-md-3">
                        <small className="text-muted d-block">Worksheet</small>

                        <strong>{sheetName}</strong>
                      </div>

                      <div className="col-md-3">
                        <small className="text-muted d-block">Total Rows</small>

                        <p className="badge bg-primary">{totalRows}</p>
                      </div>

                      <div className="col-md-3">
                        <small className="text-muted d-block">Valid Rows</small>

                        <p className="badge bg-success">{validRows}</p>
                      </div>

                      <div className="col-md-3">
                        <small className="text-muted d-block">
                          Invalid Rows
                        </small>

                        <p className="badge bg-danger">{invalidRows}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error */}

              {fileError && (
                <div className="alert alert-danger mt-3 mb-0">
                  <i className="ti ti-alert-circle me-2"></i>

                  {fileError}
                </div>
              )}
            </div>

            <hr
              style={{
                border: "none",
                height: "2px",
                background:
                  "linear-gradient(to right, transparent, #6d3300, #af5d00, #f26522, #f26522, #6d3300, transparent)",
                borderRadius: "999px",
                opacity: 1,
              }}
            />
 
            {/* STEP 4 */}
            <div className="row">
              <div className="col-md-6">
                <div className="row p-4 mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="rounded-circle d-flex align-items-center bg-primary text-white justify-content-center me-3"
                      style={{ width: 45, height: 45 }}
                    >
                      3
                    </div>
                    <div>
                      <h5 className="mb-1">
                        Select Brand Name
                        <span className="text-danger ms-1">*</span>
                      </h5>
                      <small className="text-muted">
                        Select the brand name where all uploaded programs will
                        be created.
                      </small>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <SearchableSelect
                      label="Select Brand Name"
                      name="project_id"
                      value={selectedProjectId}
                      options={projects.map((item: any) => ({
                        label: item.project_name,
                        value: item.project_id,
                      }))}
                      onChange={(_, value) => {
                        handleBrandChange(value);
                      }}
                      required
                      hasRowGrid={false}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="rounded-circle d-flex align-items-center bg-primary text-white justify-content-center me-3"
                      style={{ width: 45, height: 45 }}
                    >
                      4
                    </div>
                    <div>
                      <h5 className="mb-1">Select Program Name</h5>
                      <small className="text-muted">
                        Select the Program name where all uploaded programs will
                        be created.
                      </small>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <SearchableSelect
                      label="Program Name"
                      name="program"
                      value={filterData.program}
                      options={programList.map((item: any) => ({
                        label: item.program_name,
                        value: item.program_id,
                      }))}
                      onChange={handleFieldDataChange}
                      disabled={!selectedProjectId}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="rounded-circle d-flex align-items-center bg-primary text-white justify-content-center me-3"
                  style={{ width: 45, height: 45 }}
                >
                  5
                </div>
                <div>
                  <h5 className="mb-1">Validate & Upload</h5>
                  <small className="text-muted">
                    Make sure every required step has been completed before
                    uploading.
                  </small>
                </div>
              </div>

              <div className="mb-4">
                <div className="mb-2">
                  {file ? (
                    <span className="text-success">
                      <i className="ti ti-circle-check me-1" /> Excel file
                      selected
                    </span>
                  ) : (
                    <span className="text-muted">
                      <i className="ti ti-square me-1" /> Excel file not
                      selected
                    </span>
                  )}
                </div>
                <div>
                  {selectedProjectId ? (
                    <span className="text-success">
                      <i className="ti ti-circle-check me-1" /> Brand name
                      selected
                    </span>
                  ) : (
                    <span className="text-muted">
                      <i className="ti ti-square me-1" /> Brand name not
                      selected
                    </span>
                  )}
                </div>
              </div>

              {uploadResult && (
                <div
                  className={`alert mb-3 py-2 ${
                    uploadResult.type === "success"
                      ? "alert-success"
                      : "alert-danger"
                  }`}
                >
                  <i
                    className={`ti ${
                      uploadResult.type === "success"
                        ? "ti-circle-check"
                        : "ti-alert-circle"
                    } me-2`}
                  />
                  {uploadResult.message}
                </div>
              )}

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>
                <button
                  className="btn btn-primary btn-lg"
                  disabled={
                    !file || !selectedProjectId || excelRows.length === 0
                  }
                  onClick={handleBulkUpload}
                >
                  {isUploading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-upload me-2"></i>
                      Upload Programs
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDepartment; 